// Small "Deposit £X · <status>" badge shown next to an appointment's
// treatment name in the Overview "Upcoming appointments" / "Past
// appointments" tables and the Treatments-tab appointments list.
//
// Contract: Appointment objects (lead appointment lists) gain a
// `deposit_summary` field — { amount, payment_type, status, paid_at } or
// null. As of 2026-09-26 the backend only ever derives `status` as 'paid'
// or 'awaiting' (from paid_at) — 'received'/'expired'/'not_paid' are not
// currently emitted, but are kept mapped below for forward-compatibility in
// case the backend derivation grows to distinguish them later. Tolerant of
// the field being entirely absent (renders nothing).
import PropTypes from 'prop-types';
import Label from '../Label';

const STATUS_CONFIG = {
    paid: { label: 'Paid', color: 'success' },
    received: { label: 'Received', color: 'success' },
    awaiting: { label: 'Awaiting', color: 'warning' },
    expired: { label: 'Expired', color: 'error' },
    // 'not_paid' (and any unrecognised value) — nothing to show, this isn't
    // a state worth calling out inline the way an active/completed deposit is.
};

const formatAmount = (n) => {
    const num = Number(n) || 0;
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

export default function AppointmentDepositBadge({ depositSummary }) {
    if (!depositSummary) return null;
    const cfg = STATUS_CONFIG[depositSummary.status];
    if (!cfg) return null;
    return (
        <Label color={cfg.color} variant="ghost" data-testid="appointment-deposit-badge">
            Deposit £{formatAmount(depositSummary.amount)} · {cfg.label}
        </Label>
    );
}

AppointmentDepositBadge.propTypes = {
    depositSummary: PropTypes.shape({
        amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        payment_type: PropTypes.string,
        status: PropTypes.string,
        paid_at: PropTypes.string,
    }),
};

AppointmentDepositBadge.defaultProps = {
    depositSummary: null,
};
